import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Row, Col, Table, Button, Spinner, Alert, Modal, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { adminApi, extractError } from '../api/client';
import { fmtRelative } from '../lib/lookups';
import { useToast } from '../contexts/ToastContext';

function StatusPill({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`status-badge ${isActive ? 'status-published' : 'status-archived'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

export default function StatesListPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(60);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      const res = await adminApi.get('/admin/states', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, status]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await adminApi.delete(`/admin/states/${deleteTarget.id}`);
      const cnt = res.data?.deleted_counties || 0;
      toast.success(`${deleteTarget.name} deleted${cnt ? ` (and ${cnt} counties)` : ''}`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <PageHeader
        title="States"
        subtitle={`${total} state${total === 1 ? '' : 's'} · Locations directory for the national voter guide`}
        actions={
          <Button variant="primary" onClick={() => navigate('/admin/states/new')} data-testid="states-create-button">
            <i className="bi bi-plus-lg me-1" /> New state
          </Button>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col xs={12} md={6}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control
                    placeholder="Name, abbreviation, slug…"
                    value={q}
                    onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    data-testid="states-search-input"
                  />
                </InputGroup>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="states-status-select">
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-geo-alt" />
              <div>No states yet.</div>
              <Button variant="primary" className="mt-2" onClick={() => navigate('/admin/states/new')}>Add your first state</Button>
            </div>
          ) : (
            <>
              <Table hover responsive size="sm" className="mb-0" data-testid="states-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Abbreviation</th>
                    <th>Slug</th>
                    <th>Counties</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} data-testid={`state-row-${s.id}`}>
                      <td>
                        <Link to={`/admin/states/${s.id}`} className="text-decoration-none fw-semibold" data-testid={`state-name-link-${s.id}`}>
                          {s.name}
                        </Link>
                      </td>
                      <td><code className="token-mono">{s.abbreviation}</code></td>
                      <td className="text-muted" style={{ fontSize: '0.85rem' }}>{s.slug}</td>
                      <td>
                        <Link to={`/admin/states/${s.id}/counties`} className="text-decoration-none" data-testid={`state-counties-link-${s.id}`}>
                          {s.counties_count} <i className="bi bi-arrow-right" />
                        </Link>
                      </td>
                      <td><StatusPill status={s.status} /></td>
                      <td className="text-muted">{fmtRelative(s.updated_at)}</td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <Link to={`/admin/states/${s.id}/counties`}><Button size="sm" variant="outline-secondary">Counties</Button></Link>
                          <Link to={`/admin/states/${s.id}`}><Button size="sm" variant="outline-primary" data-testid={`state-edit-${s.id}`}>Edit</Button></Link>
                          <Button size="sm" variant="outline-danger" onClick={() => setDeleteTarget(s)} data-testid={`state-delete-${s.id}`}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} states</div>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton><Modal.Title>Delete state</Modal.Title></Modal.Header>
        <Modal.Body>
          Delete <strong>{deleteTarget?.name}</strong>? This will also remove all <strong>{deleteTarget?.counties_count ?? 0}</strong> counties under this state. This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-state-button">
            {deleting ? <Spinner size="sm" animation="border" /> : 'Delete state & counties'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
